// Shared price/number formatting for the signup flow. Used by both
// plan-picker.tsx (plan selection) and payment-step.tsx (order summary) so
// the two screens can never disagree on how a plan's price is displayed.

// Locale is pinned (not left to the ambient default) so output is
// deterministic across Node/ICU builds — see formatPrice below.
const PRICE_LOCALE = 'en-US';

// A valid ISO 4217-shaped currency code: exactly three ASCII letters.
// `billing_plans.currency` is free-form text (no allow-list at the write
// boundary — see the card), so a value that does not match this shape must
// not reach `Intl.NumberFormat`. A well-formed code can still be rejected by
// Intl with a RangeError during formatter construction.
const CURRENCY_CODE_RE = /^[A-Za-z]{3}$/;

/**
 * Formats an amount stored in a currency's minor unit (the same
 * representation Stripe uses) into a human-readable price string.
 *
 * `billing_plans.currency` is free-form text and Stripe treats ~20
 * currencies (JPY, KRW, ...) as "zero-decimal" — their minor unit IS the
 * whole unit, so naively dividing by 100 understates those amounts 100x.
 * `Intl.NumberFormat` already knows each currency's minor-unit exponent
 * (`resolvedOptions().minimumFractionDigits`), so we ask it rather than
 * hard-coding a zero-decimal currency list ourselves, then format the
 * derived whole-unit amount with that same formatter instance so the
 * divisor and the displayed symbol/grouping can never disagree.
 *
 * A currency value that is not a well-formed 3-letter code (e.g. an empty
 * string or free text some platform admin typed into `billing_plans`) is
 * handled before formatter construction. If Intl rejects a well-formed code
 * with a RangeError during render, fall back to the pre-fix `CODE amount`
 * rendering, assuming a two-decimal (cents) minor unit — the same assumption
 * the old code made, and the best guess available for a code the formatter
 * cannot identify.
 */
export function formatPrice(minorUnitAmount: number, currency: string): string {
  const upperCurrency = currency.toUpperCase();
  if (!CURRENCY_CODE_RE.test(upperCurrency)) {
    const dollars = minorUnitAmount / 100;
    const formatted = dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2);
    return `${upperCurrency} ${formatted}`;
  }

  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat(PRICE_LOCALE, {
      style: 'currency',
      currency: upperCurrency,
    });
  } catch (err) {
    if (!(err instanceof RangeError)) {
      throw err;
    }
    // A 3-letter code that Intl still does not recognize as a currency.
    const dollars = minorUnitAmount / 100;
    const formatted = dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2);
    return `${upperCurrency} ${formatted}`;
  }
  const minimumFractionDigits = formatter.resolvedOptions().minimumFractionDigits ?? 2;
  const wholeUnitAmount = minorUnitAmount / 10 ** minimumFractionDigits;
  return formatter.format(wholeUnitAmount);
}

export function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}
